import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { createFeedback } from '../../redux/QuestionBank/FeedbackAction';

const FEEDBACK_TYPES = [
  { value: 'typo', label: 'Typo', icon: 'fa-spell-check' },
  { value: 'incorrect_answer', label: 'Wrong Answer', icon: 'fa-times-circle' },
  { value: 'unclear_question', label: 'Unclear', icon: 'fa-question-circle' },
  { value: 'incorrect_explanation', label: 'Bad Explanation', icon: 'fa-book' },
  { value: 'image_issue', label: 'Image Issue', icon: 'fa-image' },
  { value: 'other', label: 'Other', icon: 'fa-comment' }
];

const QuestionFeedbackModal = ({
  isOpen,
  toggle,
  questionId,
  questionCode,
  passageId,
  testId,
  layoutMode = 'standard' // 'standard' or 'pmr'
}) => {
  const dispatch = useDispatch();
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);
  const userName = useSelector(state => state.loginAuth?.userDetails?.name);
  const { createLoading } = useSelector(state => state.questionBankFeedback || {});

  const [feedbackType, setFeedbackType] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const isBoardStyle = layoutMode === 'pmr';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!feedbackType) {
      setError('Please select a feedback type.');
      return;
    }

    if (!description.trim()) {
      setError('Please provide a description.');
      return;
    }

    if (description.trim().length < 10) {
      setError('Please provide more details (at least 10 characters).');
      return;
    }

    const result = await dispatch(createFeedback({
      questionId,
      questionCode,
      passageId,
      feedbackType,
      description: description.trim(),
      userHandle,
      userName,
      testId
    }));

    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Feedback Submitted',
        text: 'Thank you for your feedback. Our team will review it.',
        timer: 3000,
        showConfirmButton: false
      });
      handleClose();
    } else {
      setError(result.error || 'Failed to submit feedback. Please try again.');
    }
  };

  const handleClose = () => {
    setFeedbackType('');
    setDescription('');
    setError('');
    toggle();
  };

  // Board Style (PMR) Design
  if (isBoardStyle) {
    return (
      <Modal isOpen={isOpen} toggle={handleClose} size="md" className="modal-dialog-centered">
        {/* Sky Blue Header */}
        <div style={{ backgroundColor: '#5BA4D9', color: '#ffffff', padding: '12px 16px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span style={{ fontWeight: '600', fontSize: '1rem' }}>
              Question Feedback
            </span>
            <span
              onClick={handleClose}
              style={{ cursor: 'pointer', fontSize: '1.2rem' }}
            >
              &times;
            </span>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <div style={{ backgroundColor: '#ffffff', padding: '16px' }}>
            {error && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '10px 12px',
                marginBottom: '12px',
                border: '1px solid #f5c6cb',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            {questionCode && (
              <div style={{
                backgroundColor: '#e9ecef',
                padding: '8px 12px',
                marginBottom: '16px',
                fontSize: '0.85rem',
                color: '#333'
              }}>
                <strong>Question:</strong> {questionCode}
              </div>
            )}

            {/* Feedback Type Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', color: '#333' }}>
                Feedback Type <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {FEEDBACK_TYPES.map((type) => (
                  <div
                    key={type.value}
                    onClick={() => setFeedbackType(type.value)}
                    style={{
                      padding: '10px 8px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: feedbackType === type.value ? '2px solid #5BA4D9' : '1px solid #dee2e6',
                      backgroundColor: feedbackType === type.value ? '#e3f2fd' : '#ffffff',
                      fontSize: '0.8rem',
                      color: '#333',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <i className={`fas ${type.icon}`} style={{
                      display: 'block',
                      marginBottom: '4px',
                      color: feedbackType === type.value ? '#5BA4D9' : '#6c757d'
                    }}></i>
                    {type.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', color: '#333' }}>
                Description <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <textarea
                rows="4"
                placeholder="Describe the issue..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #dee2e6',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#6c757d' }}>
                {description.length}/1000
              </div>
            </div>
          </div>

          {/* Footer with Board Style buttons */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px 16px',
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={createLoading}
              style={{
                padding: '8px 20px',
                backgroundColor: '#6c757d',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createLoading}
              style={{
                padding: '8px 20px',
                backgroundColor: '#5BA4D9',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              {createLoading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </Form>
      </Modal>
    );
  }

  // Prep Style (Standard) Design
  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <ModalHeader toggle={handleClose} className="border-bottom">
        <i className="fas fa-comment-alt mr-2 text-warning"></i>
        Question Feedback
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </Alert>
          )}

          {questionCode && (
            <p className="text-muted mb-3">
              <small>
                <strong>Question:</strong> {questionCode}
              </small>
            </p>
          )}

          <FormGroup>
            <Label for="feedbackType" className="font-weight-bold">
              Feedback Type <span className="text-danger">*</span>
            </Label>
            <div className="d-flex flex-wrap gap-2">
              {FEEDBACK_TYPES.map((type) => (
                <Badge
                  key={type.value}
                  color={feedbackType === type.value ? 'primary' : 'light'}
                  className={`py-2 px-3 mr-2 mb-2 ${feedbackType === type.value ? '' : 'text-dark border'}`}
                  style={{
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: feedbackType === type.value ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setFeedbackType(type.value)}
                  pill
                >
                  <i className={`fas ${type.icon} mr-1`}></i>
                  {type.label}
                </Badge>
              ))}
            </div>
          </FormGroup>

          <FormGroup>
            <Label for="description" className="font-weight-bold">
              Description <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              id="description"
              rows="4"
              placeholder="Describe the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
            />
            <small className="text-muted text-right d-block">
              {description.length}/1000
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-top">
          <Button color="secondary" onClick={handleClose} disabled={createLoading}>
            Cancel
          </Button>
          <Button color="warning" type="submit" disabled={createLoading}>
            {createLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane mr-2"></i>
                Submit Feedback
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default QuestionFeedbackModal;
